import React, { useContext, useEffect, useState } from 'react';
import { FormControl, MenuItem, Select } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  VFGlobalContext,
  ADD_EDITING_DATA,
} from '@mummssoftware/global-context/VFGlobalContext';
import {
  Daily,
  Refused,
  ZeroFreq,
  Discontinued,
  Weekly,
} from '@mummssoftware/utils/frequencies/frequency-constants';
import { isEmptyArray } from '@mummssoftware/utils/utils';
import { isFreqNoEditDay, isFreqNotEditable } from './utils';
import { resolveInlineDaysSeed } from './cellEditState';
import { CPFContext } from '../../containers/Context/CPFContext';
import { CarePlanFrequencyContext } from '../../context';
import { DaysOfWeekColumn, MenuItemContainer } from '../layout/WrappedDivs';
import { daysOfTheWeek } from '../../containers/Context/constants';
import { SpecificDays } from '../../utils/constant';
import {
  getStagedOrderEntry,
  buildOrderEntryPayload,
  resolveCarePlanForRow,
} from './orderEntryHelpers';
import {
  getWidgetEditMode,
  getCellEditRights,
  CPF_EDIT_MODE,
  CPF_SAVE_VIA,
  CpfEditMode,
} from '../../editPolicy';

export const EditDaysOfWeekCell = (props: any) => {
  const { editingRoleGroupId, isCPFAgency, isPRNOrderAgency } = useContext(
    CPFContext,
  );
  const { readOnly } = useContext(CarePlanFrequencyContext);
  const { VFordersMode, dispatchVF, VForderData } = useContext(VFGlobalContext);
  const { data, rgHash, isEditable, editMode: editModeProp } = props;
  const { roleGroupId, name, orderEntryId } = data;

  const mode: CpfEditMode =
    editModeProp ||
    getWidgetEditMode({
      isCPFAgency: !!isCPFAgency,
      isPRNOrderAgency: !!isPRNOrderAgency,
      VFordersMode,
    });

  const isActiveRow =
    !!isEditable ||
    editModeProp === CPF_EDIT_MODE.ORDER ||
    (editingRoleGroupId != null &&
      String(editingRoleGroupId) === String(roleGroupId));

  const stagedEntry = getStagedOrderEntry(
    data,
    VForderData,
    VFordersMode,
    roleGroupId,
  );
  const carePlan =
    resolveCarePlanForRow(data, rgHash) ||
    rgHash[roleGroupId?.toString()]?.frequencies?.dummy;

  const periodForRights =
    stagedEntry?.period ||
    data.stagedPeriod ||
    carePlan?.editPeriod ||
    carePlan?.period;

  const rights = getCellEditRights({
    mode,
    column: SpecificDays,
    isActiveRow,
    carePlan,
    readOnly,
    period: periodForRights,
  });
  const { show: displayEdit, write: allowWrite } = rights;

  const dispatchOrderUpdate = (fields: Record<string, any>) => {
    if (rights.saveVia !== CPF_SAVE_VIA.ORDER) return;
    dispatchVF({
      type: ADD_EDITING_DATA,
      payload: buildOrderEntryPayload(
        { roleGroupId, name, orderEntryId },
        fields,
      ),
    });
  };

  const useStyles = makeStyles((theme) => ({
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    menuPaper: {
      maxHeight: 200,
    },
  }));
  const classes = useStyles();

  const [localDaysOfWeek, setLocalDaysOfWeek] = useState<string[]>([]);
  const stagedPeriod = stagedEntry?.period || data.stagedPeriod;

  useEffect(() => {
    if (!allowWrite) return;
    if (stagedEntry) {
      const daysWeekData = stagedEntry.daysOfWeek?.split(',');
      if (daysWeekData) {
        if (isEmptyArray(daysWeekData)) {
          setLocalDaysOfWeek([]);
        } else {
          setLocalDaysOfWeek(
            daysWeekData || carePlan?.editDaysOfWeek?.split(',') || [],
          );
        }
      }
    } else if (data.stagedDaysOfWeek) {
      setLocalDaysOfWeek(data.stagedDaysOfWeek.split(',').filter(Boolean));
    } else if (carePlan) {
      // Prefer in-progress edit* so remount after period redraw keeps picks.
      const seeded = resolveInlineDaysSeed(carePlan);
      setLocalDaysOfWeek(seeded.localDays);
      carePlan.editDaysOfWeek = seeded.nextEditDaysOfWeek;
    }
  }, [
    VForderData,
    editingRoleGroupId,
    stagedEntry,
    data.stagedDaysOfWeek,
    allowWrite,
  ]);

  useEffect(() => {
    if (!allowWrite) return;
    if (stagedEntry?.period && carePlan?.daysOfWeek && localDaysOfWeek.length) {
      if (!stagedEntry.daysOfWeek && carePlan.daysOfWeek) {
        setLocalDaysOfWeek([]);
      }
    }
  }, [VForderData, stagedEntry, allowWrite, localDaysOfWeek.length]);

  useEffect(() => {
    if (!allowWrite) return;
    if (stagedEntry?.period && localDaysOfWeek.length) {
      if (
        stagedEntry.period === Daily ||
        stagedEntry.period === Refused ||
        stagedEntry.period === ZeroFreq ||
        stagedEntry.period === Discontinued
      ) {
        setLocalDaysOfWeek([]);
        dispatchOrderUpdate({ daysOfWeek: undefined });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [VForderData, stagedEntry, allowWrite]);

  const localDaysOfWeekUpdate = (target: HTMLElement) => {
    if (!allowWrite) return;
    if (localDaysOfWeek.includes(target.id)) {
      let localDaysOfWeekCopy = [...localDaysOfWeek];
      localDaysOfWeekCopy = localDaysOfWeekCopy.filter((x) => x !== target.id);

      localDaysOfWeekCopy.sort(function sort(a, b) {
        return daysOfTheWeek.indexOf(a) - daysOfTheWeek.indexOf(b);
      });

      if (carePlan) carePlan.editDaysOfWeek = localDaysOfWeekCopy.toString();
      setLocalDaysOfWeek(localDaysOfWeekCopy);
      dispatchOrderUpdate({ daysOfWeek: localDaysOfWeekCopy.toString() });
      return;
    }
    const isWeekly =
      stagedEntry?.period === Weekly ||
      data.stagedPeriod === Weekly ||
      carePlan?.editPeriod === Weekly ||
      carePlan?.period === Weekly;
    if (isWeekly) {
      const frequency =
        stagedEntry?.frequency ??
        data.stagedFrequency ??
        carePlan?.editFrequency ??
        carePlan?.frequency;

      if (localDaysOfWeek.length + 1 > frequency) {
        const localDaysOfWeekCopy = [...localDaysOfWeek];
        localDaysOfWeekCopy.pop();
        // eslint-disable-next-line no-unused-expressions
        localDaysOfWeekCopy.push(target.id);
        localDaysOfWeekCopy.sort(function sort(a, b) {
          return daysOfTheWeek.indexOf(a) - daysOfTheWeek.indexOf(b);
        });
        setLocalDaysOfWeek(localDaysOfWeekCopy);
        if (carePlan) carePlan.editDaysOfWeek = localDaysOfWeekCopy.toString();
        dispatchOrderUpdate({ daysOfWeek: localDaysOfWeekCopy.toString() });
        return;
      }
    }

    const localDaysOfWeekCopy = [...localDaysOfWeek];
    // eslint-disable-next-line no-unused-expressions
    localDaysOfWeekCopy.push(target.id);
    localDaysOfWeekCopy.sort(function sort(a, b) {
      return daysOfTheWeek.indexOf(a) - daysOfTheWeek.indexOf(b);
    });
    setLocalDaysOfWeek(localDaysOfWeekCopy);
    if (carePlan) carePlan.editDaysOfWeek = localDaysOfWeekCopy.toString();
    dispatchOrderUpdate({ daysOfWeek: localDaysOfWeekCopy.toString() });
  };

  const listDaysOfWeekMenuItems = () =>
    daysOfTheWeek.map((dayOfWeekString) => (
      <MenuItemContainer
        dayOfWeekString={dayOfWeekString}
        localDaysOfWeek={localDaysOfWeek}
        key={dayOfWeekString}
      >
        <MenuItem
          onClick={({ target }) => localDaysOfWeekUpdate(target as HTMLElement)}
          id={dayOfWeekString}
        >
          {dayOfWeekString}
        </MenuItem>
      </MenuItemContainer>
    ));

  useEffect(() => {
    if (
      allowWrite &&
      carePlan &&
      String(editingRoleGroupId) === String(roleGroupId) &&
      isFreqNoEditDay(carePlan.editPeriod)
    ) {
      carePlan.editDaysOfWeek = '';
      setLocalDaysOfWeek([]);
    }
  }, [
    editingRoleGroupId,
    carePlan?.editDaysOfWeek,
    carePlan?.editPeriod,
    roleGroupId,
    allowWrite,
  ]);

  if (displayEdit) {
    return (
      <DaysOfWeekColumn>
        <div>{localDaysOfWeek.toString()}</div>
        <FormControl>
          <Select
            style={{
              fontSize: '.9rem',
            }}
            MenuProps={{
              classes: {
                paper: classes.menuPaper,
              },
            }}
            value=""
            defaultValue=""
          >
            {listDaysOfWeekMenuItems()}
          </Select>
        </FormControl>
      </DaysOfWeekColumn>
    );
  }
  // Daily / Refused / etc. — never show leftover weekday text.
  if (isFreqNotEditable(periodForRights || null)) {
    return <div></div>;
  }
  if (stagedEntry?.daysOfWeek || data.stagedDaysOfWeek) {
    return <div>{stagedEntry?.daysOfWeek || data.stagedDaysOfWeek}</div>;
  }
  if (stagedPeriod) {
    return <div>{localDaysOfWeek.toString()}</div>;
  }
  return <div>{carePlan?.daysOfWeek || ''}</div>;
};
